import React, { useEffect } from 'react';
import BrLocationReviewLimitsForm from './BrLocationReviewLimitsForm';
import { Box, Card, CardContent, Tab, Tabs, IconButton, Dialog, DialogContent } from '@mui/material';
import SwipeableViews from 'react-swipeable-views';
import BrLocationReviewPricingForm from './BrLocationReviewPricingForm';
import { Location } from 'types/location.type';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { getUwBRPropertyValues, getUwBRReview } from 'application/redux/selectors';
import BRPropertyModel from './BRPropertyModel';
import { validateLocationReview } from '../Helpers/validations';
import { boolean } from 'yup';

interface Props {
    readOnly: boolean;
    data: any;
    node: any;
    api: any;
    onOtherLocationReviewsUpdated: () => void;
}

const BrLocationReviewDetailRenderer: React.FC<Props> = (props) => {
    const { data, readOnly, onOtherLocationReviewsUpdated } = props;

    // Global State
    const uwBRLocationReviews = useSelector(getUwBRPropertyValues);
    const uwReview = useSelector(getUwBRReview);

    // Local State
    const [tabIndex, setTabIndex] = React.useState(0);
    const [fullscreen, setFullscreen] = React.useState(false);
    const [locationBrReview, setLocationBrReview] = React.useState<BRPropertyModel | null>(null);
    const swipeableViewsRef = React.useRef<any>(null);

    // Initialize location review when reviews or data change
    useEffect(() => {
        if (uwBRLocationReviews && uwBRLocationReviews.length > 0 && data) {
            const review = uwBRLocationReviews.find((r) => r && r.locationId === data.id);
            setLocationBrReview(review || null);
        }
    }, [uwBRLocationReviews, data, uwReview]);

    useEffect(() => {
        const timer = setTimeout(() => {
            swipeableViewsRef?.current?.updateHeight();
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    // Sync handler for both forms (inline and dialog popover)
    const handleUpdateLocationBrReview = (updatedReview: BRPropertyModel) => {
        setLocationBrReview(updatedReview);
    };

    const view = (
        <>
            <Tabs value={tabIndex} variant={'fullWidth'} onChange={(e, val) => setTabIndex(val)}>
                <Tab label={'Limits'} />
                <Tab label={'Premiums'} />
            </Tabs>
            <SwipeableViews
                index={tabIndex}
                onChangeIndex={(val) => setTabIndex(val)}
                style={{ paddingTop: '3rem' }}
                animateHeight={true}
                ref={swipeableViewsRef}
            >
                <BrLocationReviewLimitsForm
                    location={data}
                    locationBrReview={locationBrReview}
                    onChange={handleUpdateLocationBrReview}
                    readOnly={readOnly}
                />
                <BrLocationReviewPricingForm
                    location={data}
                    locationBrReview={locationBrReview}
                    onChange={handleUpdateLocationBrReview}
                    readOnly={readOnly}
                    onOtherLocationReviewsUpdated={onOtherLocationReviewsUpdated}
                />
            </SwipeableViews>
        </>
    );

    const defaultContent = (
        <Card sx={{ width: '95%', mt: 2, ml: 'auto', mr: 'auto', position: 'relative' }}>
            <CardContent>
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    {!fullscreen && (
                        <IconButton aria-label="Fullscreen" onClick={() => setFullscreen(true)}>
                            <FullscreenIcon />
                        </IconButton>
                    )}
                </Box>
                {view}
            </CardContent>
        </Card>
    );

    return (
        <>
            {!!locationBrReview && (
                <div style={{ paddingBottom: '2rem' }}>
                    {defaultContent}
                    <Dialog
                        open={fullscreen}
                        onClose={() => setFullscreen(false)}
                        fullScreen
                        PaperProps={{ sx: { background: 'transparent', boxShadow: 'none' } }}
                    >
                        <DialogContent sx={{ p: 0 }}>
                            <Box
                                sx={{
                                    width: '100vw',
                                    height: '100vh',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Card sx={{ width: '90vw', height: '90vh', overflow: 'auto', position: 'relative' }}>
                                    <CardContent>
                                        <Box sx={{ position: 'absolute', top: 16, right: 24, zIndex: 10 }}>
                                            <IconButton aria-label="Close" onClick={() => setFullscreen(false)}>
                                                <CloseIcon />
                                            </IconButton>
                                        </Box>
                                        {view}
                                    </CardContent>
                                </Card>
                            </Box>
                        </DialogContent>
                    </Dialog>
                </div>
            )}
        </>
    );
};

export default BrLocationReviewDetailRenderer;
